import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Spinner,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Copy, ExternalLink } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { formatBRL, formatDate } from "../../lib/format";

export const Route = createFileRoute("/_app/billing")({
  component: BillingPage,
});

type BillingMethod = "PIX" | "BOLETO" | "CREDIT_CARD";
type BillingCycle = "MONTHLY" | "YEARLY";
type PaidPlanId = "starter" | "pro" | "scale";

const STATUS_LABEL: Record<
  string,
  { label: string; variant: "success" | "warning" | "danger" | "accent" }
> = {
  trialing: { label: "Trial", variant: "accent" },
  active: { label: "Ativo", variant: "success" },
  past_due: { label: "Pagamento atrasado", variant: "warning" },
  canceled: { label: "Encerrado", variant: "danger" },
};

const METHODS: Array<{ id: BillingMethod; label: string; hint: string }> = [
  { id: "PIX", label: "Pix", hint: "Confirma na hora" },
  { id: "BOLETO", label: "Boleto", hint: "Compensa em alguns dias" },
  { id: "CREDIT_CARD", label: "Cartão", hint: "Página segura da Asaas" },
];

function apiErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object" && "value" in error) {
    const value = (error as { value: unknown }).value;
    if (
      value &&
      typeof value === "object" &&
      "error" in value &&
      typeof (value as { error: unknown }).error === "string"
    ) {
      return (value as { error: string }).error;
    }
  }
  return "Não foi possível abrir a cobrança";
}

function BillingPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [method, setMethod] = useState<BillingMethod>("PIX");
  const [cycle, setCycle] = useState<BillingCycle>("MONTHLY");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [lastCheckout, setLastCheckout] = useState<{
    invoiceUrl: string | null;
    bankSlipUrl: string | null;
    pixCopyPaste: string | null;
    plan: string;
  } | null>(null);

  const billing = useQuery({
    queryKey: ["billing"],
    queryFn: async () => unwrap(await api.billing.get()),
  });

  const cancel = useMutation({
    mutationFn: async () => unwrap(await api.billing.cancel.post()),
    onSuccess: () => {
      setCancelOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
      toast.add({
        type: "success",
        title: "Cancelamento agendado",
        description: "A sincronização segue até o fim do período já pago.",
      });
    },
    onError: (error) => {
      toast.add({ type: "error", title: apiErrorMessage(error) });
    },
  });

  const resume = useMutation({
    mutationFn: async () => unwrap(await api.billing.resume.post()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
      toast.add({
        type: "success",
        title: "Plano retomado",
        description: "A próxima cobrança segue na data do período atual.",
      });
    },
    onError: (error) => {
      toast.add({ type: "error", title: apiErrorMessage(error) });
    },
  });

  const checkout = useMutation({
    mutationFn: async (plan: PaidPlanId) =>
      unwrap(
        await api.billing.checkout.post({
          plan,
          billingType: method,
          cpfCnpj,
          cycle,
        }),
      ),
    onSuccess: (data) => {
      setLastCheckout({
        invoiceUrl: data.invoiceUrl,
        bankSlipUrl: data.bankSlipUrl,
        pixCopyPaste: data.pixCopyPaste,
        plan: data.plan,
      });
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
      if (data.invoiceUrl) window.open(data.invoiceUrl, "_blank", "noopener");
      toast.add({
        type: "success",
        title: "Cobrança aberta",
        description: "Conclua o pagamento para ativar o plano.",
      });
    },
    onError: (error) => {
      toast.add({ type: "error", title: apiErrorMessage(error) });
    },
  });

  if (billing.isLoading || !billing.data) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-5 text-ink-muted" />
      </div>
    );
  }

  const data = billing.data;
  const status = STATUS_LABEL[data.status] ?? STATUS_LABEL.trialing!;
  const internal = data.plan === "internal";
  const limitLabel = (value: number | null, unit: string) =>
    value == null ? `Ilimitado em ${unit}` : `${value} ${unit}`;

  return (
    <>
      <PageHeader
        title="Plano"
        description="Trial, assinatura e uso deste mês. O pagamento não passa pelo Bridge."
      />

      {data.syncBlocked && (
        <Card className="mb-5 border-warning/30 bg-warning-subtle">
          <CardContent className="p-4">
            <p className="text-[0.8125rem] font-semibold text-ink">
              Sincronização pausada
            </p>
            <p className="mt-1 text-2xs text-ink-muted">
              {data.blockReason === "trial_expired"
                ? "O trial acabou. Assine abaixo para voltar a importar e enviar estoque."
                : data.blockReason === "past_due"
                  ? "Há um pagamento em atraso. Conclua a cobrança para retomar a sincronização."
                  : data.blockReason === "canceled"
                    ? "A assinatura foi encerrada. Assine de novo para retomar."
                    : "A sincronização está pausada. Pedidos e anúncios já importados continuam visíveis."}
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="mb-5">
        <CardHeader>
          <CardTitle>Situação atual</CardTitle>
          <Badge variant={status.variant} dot>
            {status.label}
          </Badge>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className="text-2xs text-ink-muted">Plano</p>
            <p className="mt-0.5 text-[0.8125rem] font-medium text-ink">
              {data.planLabel}
            </p>
            {data.grandfathered && data.priceCents != null && (
              <p className="mt-0.5 text-2xs text-ink-muted">
                Contrato anterior, {formatBRL(data.priceCents / 100)}/mês, até
                trocar de plano.
              </p>
            )}
          </div>
          <div>
            <p className="text-2xs text-ink-muted">Lojas</p>
            <p className="mt-0.5 text-[0.8125rem] font-medium text-ink">
              {data.limits.accounts == null
                ? `${data.usage.accounts} conectadas`
                : `${data.usage.accounts} de ${data.limits.accounts}`}
            </p>
          </div>
          <div>
            <p className="text-2xs text-ink-muted">Pedidos neste mês</p>
            <p className="mt-0.5 text-[0.8125rem] font-medium text-ink">
              {data.limits.ordersPerMonth == null
                ? `${data.usage.ordersThisMonth}`
                : `${data.usage.ordersThisMonth} de ${data.limits.ordersPerMonth}`}
            </p>
            <p className="mt-0.5 text-2xs text-ink-muted">
              Média {data.usage.ordersAverage} em {data.usage.averageMonths}{" "}
              {data.usage.averageMonths === 1 ? "mês" : "meses"}
              {data.usage.excessCents > 0
                ? ` · excedente ${formatBRL(data.usage.excessCents / 100)}`
                : ""}
            </p>
          </div>
          {data.currentPeriodEnd && (
            <div className="sm:col-span-3">
              <p className="text-2xs text-ink-muted">
                {data.cancelAtPeriodEnd
                  ? "Encerra em"
                  : data.status === "trialing"
                    ? "Trial até"
                    : "Período atual até"}{" "}
                {formatDate(data.currentPeriodEnd)}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {internal ? (
        <Card>
          <CardContent className="p-5">
            <p className="text-[0.8125rem] font-semibold text-ink">
              Plano interno
            </p>
            <p className="mt-1 text-2xs text-ink-muted">
              Esta organização não entra em cobrança. Lojas e pedidos ficam sem
              teto.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {data.usage.ordersWarning && (
            <Card className="mb-4 border-warning/30 bg-warning-subtle">
              <CardContent className="p-4">
                <p className="text-[0.8125rem] font-semibold text-ink">
                  Pedidos perto da faixa
                </p>
                <p className="mt-1 text-2xs text-ink-muted">
                  Este mês já passou de 80% dos pedidos inclusos. O que passar
                  da média de {data.usage.averageMonths}{" "}
                  {data.usage.averageMonths === 1 ? "mês" : "meses"} sai a{" "}
                  {formatBRL(data.excessOrderCents / 100)} por pedido, e a
                  importação continua.{" "}
                  {data.usage.suggestedPlan
                    ? "A faixa de cima já sai mais barata do que pagar o excedente."
                    : "Se a média subir, a próxima faixa pode sair mais barata."}
                </p>
              </CardContent>
            </Card>
          )}

          <div className="mb-4 flex flex-wrap gap-2">
            {(
              [
                ["MONTHLY", "Mensal"],
                ["YEARLY", "Anual −15%"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setCycle(id)}
                className={
                  cycle === id
                    ? "rounded-control border border-ember/40 bg-ember-subtle px-2.5 py-1.5 text-2xs font-medium text-ink"
                    : "rounded-control border border-line bg-surface-raised px-2.5 py-1.5 text-2xs font-medium text-ink-secondary"
                }
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mb-4 grid gap-3 sm:grid-cols-3">
            {data.catalog.map((plan) => {
              const current = data.plan === plan.id;
              return (
                <Card
                  key={plan.id}
                  className={current ? "border-ember/40" : undefined}
                >
                  <CardContent className="flex h-full flex-col gap-3 p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[0.8125rem] font-semibold text-ink">
                          {plan.label}
                        </p>
                        <p className="mt-1 text-lg font-semibold tracking-tight text-ink">
                          {formatBRL(
                            (cycle === "YEARLY"
                              ? Math.round((plan.annualPriceCents ?? 0) / 12)
                              : (plan.priceCents ?? 0)) / 100,
                          )}
                          <span className="ml-1 text-2xs font-medium text-ink-muted">
                            {cycle === "YEARLY" ? "/mês no anual" : "/mês"}
                          </span>
                        </p>
                      </div>
                      {current && (
                        <Badge variant="accent">Plano atual</Badge>
                      )}
                    </div>
                    <ul className="space-y-1 text-2xs text-ink-secondary">
                      <li className="flex items-center gap-1.5">
                        <Check className="size-3.5 text-ember" />
                        {limitLabel(plan.accounts, "lojas")}
                      </li>
                      <li className="flex items-center gap-1.5">
                        <Check className="size-3.5 text-ember" />
                        {limitLabel(plan.ordersPerMonth, "pedidos/mês")}
                      </li>
                      <li className="flex items-center gap-1.5">
                        <Check className="size-3.5 text-ember" />
                        Excedente {formatBRL(data.excessOrderCents / 100)} por
                        pedido
                      </li>
                    </ul>
                    <div className="mt-auto">
                      <Button
                        disabled={!data.checkoutEnabled || current}
                        loading={
                          checkout.isPending && checkout.variables === plan.id
                        }
                        onClick={() => checkout.mutate(plan.id as PaidPlanId)}
                      >
                        {current ? "Já é o seu plano" : "Assinar"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {!data.checkoutEnabled && (
                <p className="text-2xs text-ink-muted">
                  A cobrança ainda não está ligada neste ambiente. Os planos
                  aparecem, mas o checkout fica desligado.
                </p>
              )}
              <Field>
                <FieldLabel>CPF ou CNPJ de quem paga</FieldLabel>
                <Input
                  value={cpfCnpj}
                  onChange={(event) => setCpfCnpj(event.target.value)}
                  placeholder="000.000.000-00"
                  autoComplete="off"
                />
                <FieldDescription>
                  Exigido pela Asaas. Não armazenamos o documento no Bridge.
                </FieldDescription>
              </Field>
              <div>
                <p className="mb-1.5 text-2xs font-medium tracking-wide text-ink-secondary">
                  Forma
                </p>
                <div className="flex flex-wrap gap-2">
                  {METHODS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setMethod(item.id)}
                      className={
                        method === item.id
                          ? "rounded-control border border-ember/40 bg-ember-subtle px-2.5 py-1.5 text-left transition-[border-color,background-color,transform] duration-150 ease-swift active:scale-[0.97]"
                          : "rounded-control border border-line bg-surface-raised px-2.5 py-1.5 text-left transition-[border-color,transform] duration-150 ease-swift hover:border-ink-faint active:scale-[0.97]"
                      }
                    >
                      <span className="block text-2xs font-medium text-ink">
                        {item.label}
                      </span>
                      <span className="block text-2xs text-ink-muted">
                        {item.hint}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-2xs text-ink-muted">
                Pix, boleto e cartão abrem a página da Asaas. O Bridge só recebe
                a confirmação depois.
              </p>
            </CardContent>
          </Card>

          {lastCheckout && (
            <Card className="mt-3">
              <CardContent className="flex flex-col gap-3 p-5">
                <p className="text-[0.8125rem] font-semibold text-ink">
                  Cobrança pronta
                </p>
                <p className="text-2xs text-ink-muted">
                  Depois do pagamento, o plano muda sozinho. Se a página não
                  abriu, use o link abaixo.
                </p>
                <div className="flex flex-wrap gap-2">
                  {lastCheckout.invoiceUrl && (
                    <a
                      href={lastCheckout.invoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-2xs font-medium text-ember hover:underline"
                    >
                      <ExternalLink className="size-3.5" />
                      Abrir fatura
                    </a>
                  )}
                  {lastCheckout.bankSlipUrl && (
                    <a
                      href={lastCheckout.bankSlipUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-2xs font-medium text-ember hover:underline"
                    >
                      <ExternalLink className="size-3.5" />
                      Boleto
                    </a>
                  )}
                  {lastCheckout.pixCopyPaste && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        void navigator.clipboard.writeText(
                          lastCheckout.pixCopyPaste!,
                        );
                        toast.add({
                          type: "success",
                          title: "Código Pix copiado",
                        });
                      }}
                    >
                      <Copy />
                      Copiar Pix
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!internal && data.cancelAtPeriodEnd && data.status !== "canceled" && (
        <Card className="mt-5 border-warning/30 bg-warning-subtle">
          <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[0.8125rem] font-semibold text-ink">
                Cancelamento agendado
              </p>
              <p className="mt-1 text-2xs text-ink-muted">
                A cobrança já parou. A sincronização segue até{" "}
                {data.currentPeriodEnd
                  ? formatDate(data.currentPeriodEnd)
                  : "o fim do período"}
                . Depois disso, importar e enviar estoque pausam. Pedidos e
                anúncios já importados continuam visíveis.
              </p>
            </div>
            <Button
              size="sm"
              loading={resume.isPending}
              onClick={() => resume.mutate()}
            >
              Retomar plano
            </Button>
          </CardContent>
        </Card>
      )}

      {!internal && !data.cancelAtPeriodEnd && data.status !== "canceled" && (
        <Card className="mt-5">
          <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[0.8125rem] font-semibold text-ink">
                Cancelar assinatura
              </p>
              <p className="mt-1 text-2xs text-ink-muted">
                Para de cobrar. O acesso continua até{" "}
                {data.currentPeriodEnd
                  ? formatDate(data.currentPeriodEnd)
                  : "o fim do período"}
                . Pedido de exclusão de dados: veja a política de privacidade.
              </p>
            </div>
            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
              <DialogTrigger
                render={
                  <Button variant="danger" size="sm">
                    Cancelar
                  </Button>
                }
              />
              <DialogContent
                title="Cancelar assinatura?"
                description="Confirme antes de parar a cobrança."
              >
                <div className="flex flex-col gap-4">
                  <ul className="space-y-1.5 text-2xs text-ink-secondary">
                    <li>A cobrança mensal para de ser gerada.</li>
                    <li>
                      A sincronização segue até{" "}
                      {data.currentPeriodEnd
                        ? formatDate(data.currentPeriodEnd)
                        : "o fim do período já pago"}
                      .
                    </li>
                    <li>
                      Depois dessa data, importar e enviar estoque pausam.
                      Pedidos e anúncios já importados continuam visíveis.
                    </li>
                    <li>
                      Se mudar de ideia antes dessa data, dá para retomar o
                      plano nesta página.
                    </li>
                  </ul>
                  <div className="flex justify-end gap-2">
                    <DialogClose
                      render={<Button variant="secondary">Voltar</Button>}
                    />
                    <Button
                      variant="danger"
                      loading={cancel.isPending}
                      onClick={() => cancel.mutate()}
                    >
                      Confirmar cancelamento
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}

      <p className="mt-5 text-2xs text-ink-faint">
        Precisa de mais lojas agora?{" "}
        <Link to="/connections" className="text-ink-muted hover:text-ink">
          Ver conexões
        </Link>
        {" · "}
        <Link to="/privacy" className="text-ink-muted hover:text-ink">
          Privacidade
        </Link>
      </p>
    </>
  );
}
